import Phaser from 'phaser';

import type { InstructionStage } from '../utils/instructionStages';

type InstructionInputControllerConfig = {
	scene: Phaser.Scene;
	renderScaleX: number;
	renderScaleY: number;
	getStage: () => InstructionStage;
	onStageChange: (stage: InstructionStage) => void;
	onMoveLeft?: () => void;
	onMoveRight?: () => void;
	onJump?: () => void;
	onStartForwardBoost?: () => void;
	onStopForwardBoost?: () => void;
	onStartReverseBoost?: () => void;
	onStopReverseBoost?: () => void;
};

type PointerDownState = {
	x: number;
	y: number;
	horizontalSwipeTriggered: boolean;
};

const TAP_THRESHOLD_PX = 24;
const SWIPE_THRESHOLD_PX = 36;

export class InstructionInputController {
	private readonly scene: Phaser.Scene;
	private readonly renderScaleX: number;
	private readonly renderScaleY: number;
	private readonly getStage: () => InstructionStage;
	private readonly onStageChange: (stage: InstructionStage) => void;
	private readonly onMoveLeft?: () => void;
	private readonly onMoveRight?: () => void;
	private readonly onJump?: () => void;
	private readonly onStartForwardBoost?: () => void;
	private readonly onStopForwardBoost?: () => void;
	private readonly onStartReverseBoost?: () => void;
	private readonly onStopReverseBoost?: () => void;
	private pointerDownState: PointerDownState | null = null;
	private activeSwipeBoostDirection: 'forward' | 'reverse' | null = null;

	constructor({
		scene,
		renderScaleX,
		renderScaleY,
		getStage,
		onStageChange,
		onMoveLeft,
		onMoveRight,
		onJump,
		onStartForwardBoost,
		onStopForwardBoost,
		onStartReverseBoost,
		onStopReverseBoost,
	}: InstructionInputControllerConfig) {
		this.scene = scene;
		this.renderScaleX = renderScaleX;
		this.renderScaleY = renderScaleY;
		this.getStage = getStage;
		this.onStageChange = onStageChange;
		this.onMoveLeft = onMoveLeft;
		this.onMoveRight = onMoveRight;
		this.onJump = onJump;
		this.onStartForwardBoost = onStartForwardBoost;
		this.onStopForwardBoost = onStopForwardBoost;
		this.onStartReverseBoost = onStartReverseBoost;
		this.onStopReverseBoost = onStopReverseBoost;

		this.scene.input.keyboard?.on('keydown', this.handleKeyDown, this);
		this.scene.input.keyboard?.on('keyup', this.handleKeyUp, this);
		this.scene.input.on(Phaser.Input.Events.POINTER_DOWN, this.handlePointerDown, this);
		this.scene.input.on(Phaser.Input.Events.POINTER_MOVE, this.handlePointerMove, this);
		this.scene.input.on(Phaser.Input.Events.POINTER_UP, this.handlePointerUp, this);
		this.scene.input.on(Phaser.Input.Events.POINTER_UP_OUTSIDE, this.handlePointerUp, this);
	}

	cancelActiveGesture() {
		this.pointerDownState = null;
		this.stopActiveSwipeBoost();
	}

	destroy() {
		this.stopActiveSwipeBoost();
		this.scene.input.keyboard?.off('keydown', this.handleKeyDown, this);
		this.scene.input.keyboard?.off('keyup', this.handleKeyUp, this);
		this.scene.input.off(Phaser.Input.Events.POINTER_DOWN, this.handlePointerDown, this);
		this.scene.input.off(Phaser.Input.Events.POINTER_MOVE, this.handlePointerMove, this);
		this.scene.input.off(Phaser.Input.Events.POINTER_UP, this.handlePointerUp, this);
		this.scene.input.off(Phaser.Input.Events.POINTER_UP_OUTSIDE, this.handlePointerUp, this);
		this.pointerDownState = null;
	}

	private handleKeyDown(event: KeyboardEvent) {
		if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key) || event.code === 'Space') {
			event.preventDefault();
		}

		if (event.key === 'ArrowLeft') {
			this.onMoveLeft?.();
			if (this.getStage() === 'leftRight') {
				this.onStageChange('up');
			}
			return;
		}

		if (event.key === 'ArrowRight') {
			this.onMoveRight?.();
			if (this.getStage() === 'leftRight') {
				this.onStageChange('up');
			}
			return;
		}

		if (event.code === 'Space' || event.key === ' ') {
			this.onJump?.();
			if (this.getStage() === 'space') {
				this.onStageChange('done');
			}
			return;
		}

		if (event.key === 'ArrowUp') {
			this.onStartForwardBoost?.();
			if (this.getStage() === 'up') {
				this.onStageChange('space');
			}
			return;
		}

		if (event.key === 'ArrowDown') {
			this.onStartReverseBoost?.();
			if (this.getStage() === 'up') {
				this.onStageChange('space');
			}
		}
	}

	private handleKeyUp(event: KeyboardEvent) {
		if (event.key === 'ArrowUp') {
			this.onStopForwardBoost?.();
			return;
		}

		if (event.key === 'ArrowDown') {
			this.onStopReverseBoost?.();
		}
	}

	private handlePointerDown(pointer: Phaser.Input.Pointer) {
		this.stopActiveSwipeBoost();
		this.pointerDownState = {
			x: pointer.x,
			y: pointer.y,
			horizontalSwipeTriggered: false,
		};
	}

	private handlePointerMove(pointer: Phaser.Input.Pointer) {
		if (!this.pointerDownState || !pointer.isDown) {
			return;
		}

		const deltaX = pointer.x - this.pointerDownState.x;
		const deltaY = pointer.y - this.pointerDownState.y;
		const swipeThreshold = SWIPE_THRESHOLD_PX * Math.max(this.renderScaleX, this.renderScaleY);

		if (
			!this.pointerDownState.horizontalSwipeTriggered &&
			!this.activeSwipeBoostDirection &&
			Math.abs(deltaX) >= swipeThreshold &&
			Math.abs(deltaX) > Math.abs(deltaY)
		) {
			if (deltaX < 0) {
				this.onMoveLeft?.();
			} else {
				this.onMoveRight?.();
			}
			this.pointerDownState.horizontalSwipeTriggered = true;
			if (this.getStage() === 'leftRight') {
				this.onStageChange('up');
			}
			return;
		}

		if (
			Math.abs(deltaY) < swipeThreshold ||
			Math.abs(deltaY) <= Math.abs(deltaX) ||
			this.pointerDownState.horizontalSwipeTriggered
		) {
			return;
		}

		if (deltaY < 0) {
			if (this.activeSwipeBoostDirection === 'reverse') {
				this.onStopReverseBoost?.();
			}
			if (this.activeSwipeBoostDirection !== 'forward') {
				this.activeSwipeBoostDirection = 'forward';
				this.onStartForwardBoost?.();
				if (this.getStage() === 'up') {
					this.onStageChange('space');
				}
			}
			return;
		}

		if (this.activeSwipeBoostDirection === 'forward') {
			this.onStopForwardBoost?.();
		}
		if (this.activeSwipeBoostDirection !== 'reverse') {
			this.activeSwipeBoostDirection = 'reverse';
			this.onStartReverseBoost?.();
		}
	}

	private handlePointerUp(pointer: Phaser.Input.Pointer) {
		if (!this.pointerDownState) {
			this.pointerDownState = null;
			return;
		}

		const deltaX = pointer.x - this.pointerDownState.x;
		const deltaY = pointer.y - this.pointerDownState.y;
		const swipeThreshold = SWIPE_THRESHOLD_PX * Math.max(this.renderScaleX, this.renderScaleY);
		const tapThreshold = TAP_THRESHOLD_PX * Math.max(this.renderScaleX, this.renderScaleY);

		if (this.activeSwipeBoostDirection === 'forward') {
			this.onStopForwardBoost?.();
			this.activeSwipeBoostDirection = null;
			this.pointerDownState = null;
			return;
		}

		if (this.activeSwipeBoostDirection === 'reverse') {
			this.onStopReverseBoost?.();
			this.activeSwipeBoostDirection = null;
			this.pointerDownState = null;
			return;
		}

		if (this.pointerDownState.horizontalSwipeTriggered) {
			this.pointerDownState = null;
			return;
		}

		if (Math.abs(deltaX) >= swipeThreshold && Math.abs(deltaX) > Math.abs(deltaY)) {
			if (deltaX < 0) {
				this.onMoveLeft?.();
			} else {
				this.onMoveRight?.();
			}
			if (this.getStage() === 'leftRight') {
				this.onStageChange('up');
			}
		} else if (Math.abs(deltaY) >= swipeThreshold && Math.abs(deltaY) > Math.abs(deltaX)) {
			if (deltaY < 0) {
				this.onStartForwardBoost?.();
				this.onStopForwardBoost?.();
				if (this.getStage() === 'up') {
					this.onStageChange('space');
				}
			} else {
				this.onStartReverseBoost?.();
				this.onStopReverseBoost?.();
			}
		} else if (Math.abs(deltaX) <= tapThreshold && Math.abs(deltaY) <= tapThreshold) {
			this.onJump?.();
			if (this.getStage() === 'space') {
				this.onStageChange('done');
			}
		}

		this.pointerDownState = null;
	}

	private stopActiveSwipeBoost() {
		if (this.activeSwipeBoostDirection === 'forward') {
			this.onStopForwardBoost?.();
		}
		if (this.activeSwipeBoostDirection === 'reverse') {
			this.onStopReverseBoost?.();
		}
		this.activeSwipeBoostDirection = null;
	}
}
